import { get } from 'lodash'
import qs from 'qs'
import changeCaseObject from 'change-case-object'
import moment from 'moment'
import client from './client'
import { SERVER_DATE } from 'src/constants/formats'
import { mockAirDnaResponse } from './requestMocks'
import nonJsonApiClient from 'src/api/mikasa/nonJsonApiClient'

const TODAY = moment().format(SERVER_DATE)
const ONE_YEAR_AGO = moment()
  .subtract(1, 'year')
  .format(SERVER_DATE)
const defaultDate = moment().toJSON()

export const getClientData = (clientId, includeProfile = true) => {
  let include = []

  if (includeProfile) {
    include = [
      'customer-profile',
      'customer-profile.office-profile',
      'customer-profile.office-profile.corporate-profile',
      ...include
    ]
  }

  return client.get(`clients/${clientId}?include=${include.join(',')}`)
}

export const getBuyerInfoData = (clientId, includeBuyerInfo = true) => {
  let include = []

  if (includeBuyerInfo) {
    include = ['market-interests', 'prequalifications', ...include]
  }

  return client.get(`buyers/${clientId}?include=${include.join(',')}`)
}

export const getClientAnswers = clientId =>
  client.get(`clients/${clientId}/client-answers`)

export const getCustomerProfile = profileId => {
  const include = ['office-profile', 'office-profile.corporate-profile']

  return client.get(
    `customer-profiles/${profileId}?include=${include.join(',')}`
  )
}

export const requestDigest = (buyerId, email) =>
  client.post(`buyers/${buyerId}/request_digest`, { email })

const buyerAction = verb => (id, data) =>
  client[verb](`buyers/${id}`, {
    data: {
      id,
      type: 'buyers',
      ...data
    }
  })

const patchBuyer = buyerAction('patch')
const putBuyer = buyerAction('put')

export const setMarketInterest = (clientId, zipcode) =>
  client.post(`clients/${clientId}/market-interests`, {
    data: {
      type: 'market-interests',
      attributes: { zipcode }
    }
  })

export const removeMarketInterest = (buyerId, id) =>
  client.delete(`buyers/${buyerId}/market-interests/${id}`)

export const setBuyerSettings = (id, data) =>
  patchBuyer(id, {
    attributes: changeCaseObject.paramCase(data)
  })

export const putBuyerSettings = (id, data) =>
  putBuyer(id, {
    attributes: data
  })

export const fetchMortgageRates = (term = 30) => {
  const params = qs.stringify({
    page: {
      size: 52
    },
    filter: {
      term,
      'mortgage-type': 'FIX',
      'date-range': `${ONE_YEAR_AGO}..${TODAY}`
    }
  })
  return client.get(`mortgage-rates?${params}`)
}

export const fetchLatestMortgageRates = () => {
  const params = qs.stringify({
    filter: {
      latest: true,
      available_products: true
    }
  })
  return client.get(`mortgage-rates?${params}`)
}

export const fetchSponsor = realEstateAgentId =>
  client.get(`real-estate-agents/${realEstateAgentId}/sponsor`)

export const fetchAirDna = ({ address, zipcode, validationToken }) => {
  const MOCK = process.env.REACT_APP_MOCK_AIRDNA === 'true'

  if (MOCK) {
    console.log('REACT_APP_MOCK_AIRDNA: using mock response')
    return mockAirDnaResponse()
  }

  const params = qs.stringify({
    rental_potential: {
      address,
      zipcode
    },
    validation_token: validationToken
  })

  return client.get(`search/rental_potential?${params}`)
}

export const sendQuestion = (id, body) =>
  client.post('emails', {
    email: {
      type: 'buyer/message',
      attributes: {
        buyer_id: id,
        body
      }
    }
  })

export const fetchAutoComplete = async addressData => {
  const response = await nonJsonApiClient.get(
    `/autocomplete/search-address?data[address]=${addressData}`
  )

  return response
}

const getElasticRecordsFromResponse = response =>
  get(response, 'data.hits', []).map(i => i.source)

export const marketsByBoundaryRequest = async params => {
  const response = await client.get('/markets/boundaries', { params })
  return getElasticRecordsFromResponse(response)
}

export const marketsByZipcodesRequest = async zipcodes => {
  const response = await client.get(`markets/zipcodes/${zipcodes.join(',')}`)
  return getElasticRecordsFromResponse(response)
}

export const enableHBB = (id, attrs = {}) =>
  client.patch(`clients/${id}`, {
    data: {
      type: 'clients',
      id,
      attributes: {
        'hbb-access': true,
        ...attrs
      }
    }
  })

export const createAnswer = (clientId, { question, response }) =>
  client.post(`clients/${clientId}/client-answers`, {
    data: {
      type: 'client-answers',
      attributes: {
        question,
        response
      }
    }
  })

export const updateAnswer = (clientId, { id, question, response }) =>
  client.patch(`clients/${clientId}/client-answers/${id}`, {
    data: {
      type: 'client-answers',
      id,
      attributes: {
        question,
        response
      }
    }
  })

export const getHomeInsurance = homeId =>
  client.get(`homes/${homeId}/home-insurance`)

export const createHome = ({ clientId, id }) =>
  client.post(`clients/${clientId}/homes`, {
    data: {
      type: 'homes',
      attributes: {
        'address-id': id
      }
    }
  })

export const submitLoanBalance = (loanId, value, date) =>
  client.post(`loans/${loanId}/loan-balances`, {
    data: {
      type: 'loan-balances',
      attributes: {
        source: 'user',
        value,
        'effective-date': date || defaultDate
      }
    }
  })
